import { ButtonSpeedDial, ConfirmModal, FloatingLoader } from "@/components";
import { useBoolean } from "@/hooks/useBoolean";
import { useDebounce } from "@/hooks/useDebounce";
import useProfile from "@/hooks/useProfile";
import usePromptCollection from "@/hooks/usePromptCollection";
import useSyncMutation from "@/hooks/useSyncMutation";
import { QUERY_KEYS } from "@/libraries/constant";
import { getCasesQueryOptions } from "@/queries/options/case";
import { CaseSearchParamSchema } from "@/schemas";
import { useSyncStore } from "@/stores/sync";
import { onlineManager, useSuspenseInfiniteQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useVirtualizer } from "@tanstack/react-virtual";
import upperFirst from "lodash/upperFirst";
import { useEffect, useMemo, useRef } from "react";
import { z } from "zod";
import AppBarCase from "./-components/AppBar";
import CaseCard from "./-components/CaseCard";
import EmptyCase from "./-components/Empty";

export const Route = createFileRoute("/dashboard/case/$status")({
	validateSearch: CaseSearchParamSchema,
	loader: ({ context, params }) => {
		if (onlineManager.isOnline() && !context.isRestoring) {
			return context.queryClient.ensureInfiniteQueryData(
				getCasesQueryOptions({
					status: params.status as any,
				}),
			);
		}
		return context.queryClient.getQueryData([
			QUERY_KEYS.GET_CASES,
			{
				status: params.status,
			},
		]);
	},
	params: {
		parse: (params) => ({
			status: z
				.enum(["active", "closed"])
				.default("active")
				.parse(params.status),
		}),
		stringify: ({ status }) => ({ status }),
	},
	component: RouteComponent,
});

function RouteComponent() {
	const navigate = Route.useNavigate();
	const searchQ = Route.useSearch({
		select: (state: CaseSearchParamSchema) => state.q ?? "",
	});
	const caseStatus = Route.useParams({
		select: (state) => state.status,
	});

	const caseSyncLastUnix = useSyncStore((s) => s.caseSyncLastUnix);
	const { caseSync } = useSyncMutation();

	const { profile } = useProfile();

	const {
		value: isOpenConfirmFreeCase,
		setTrue: openConfirmFreeCase,
		setFalse: closeConfirmFreeCase,
	} = useBoolean();
	const {
		value: isOpenAddNewCase,
		setTrue: openAddNewCase,
		setFalse: closeAddNewCase,
	} = useBoolean();

	const scrollRef = useRef<HTMLDivElement>(null);
	const debouncedSearch = useDebounce(searchQ, 300);

	const {
		data: cases,
		isLoading,
		isFetching,
		isFetchingNextPage,
		hasNextPage,
		fetchNextPage,
	} = useSuspenseInfiniteQuery(
		getCasesQueryOptions({
			status: caseStatus as any,
		}),
	);

	const caseItems = useMemo(() => {
		if (!cases.length) return [];
		if (debouncedSearch) {
			return cases.filter(
				(caseItem) =>
					caseItem.title
						.toLowerCase()
						.includes(debouncedSearch.toLowerCase()) ||
					caseItem.summary
						?.toLowerCase()
						?.includes(debouncedSearch.toLowerCase()) ||
					caseItem.patient?.name
						?.toLowerCase()
						?.includes(debouncedSearch.toLowerCase()) ||
					caseItem.patient?.surname
						?.toLowerCase()
						?.includes(debouncedSearch.toLowerCase()),
			);
		}
		return cases;
	}, [cases, debouncedSearch]);

	const casesVirtualizer = useVirtualizer({
		count: caseItems.length,
		gap: 12,
		overscan: 5,
		paddingStart: 16,
		paddingEnd: caseItems.length ? 295 : undefined,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => 125,
		measureElement: (el) => el.getBoundingClientRect().height,
	});
	const casesVirtualizerItems = casesVirtualizer.getVirtualItems();

	const isEmpty = useMemo(
		() => !isLoading && !isFetching && !caseItems.length && !searchQ,
		[isLoading, isFetching, searchQ, caseItems],
	);
	const isEmptySearch = useMemo(
		() => !isLoading && !isFetching && !caseItems.length && !!searchQ,
		[isLoading, isFetching, searchQ, caseItems],
	);

	const { upgradeCase } = usePromptCollection({
		currentPlan: profile?.subscription_status,
	});

	const onClickAdd = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		if (profile?._isProPlan) {
			return openAddNewCase();
		}
		if (profile?.case_left) {
			return openConfirmFreeCase();
		}
		return upgradeCase(profile?._maxCase);
	};

	const onClickSync = (e: React.MouseEvent<HTMLButtonElement>) => {
		e.preventDefault();
		return caseSync.mutate({
			last_requested_at: caseSyncLastUnix,
		});
	};

	useEffect(() => {
		const [lastItem] = [...casesVirtualizerItems].reverse();

		if (!lastItem) return;

		if (
			lastItem.index >= cases.length - 1 &&
			hasNextPage &&
			!isFetchingNextPage
		) {
			fetchNextPage();
		}
	}, [
		cases.length,
		hasNextPage,
		fetchNextPage,
		isFetchingNextPage,
		casesVirtualizerItems,
	]);

	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		if (!caseSync.isPending) {
			caseSync.mutate({
				last_requested_at: 0,
			});
		}
	}, []);

	return (
		<div className="relative flex-1 flex flex-col overflow-hidden">
			<AppBarCase variant="case" title="Cases" />
			<div className="relative flex-1 flex flex-col overflow-hidden">
				<FloatingLoader open={isLoading || isFetchingNextPage} />
				<div ref={scrollRef} className="relative h-full w-full overflow-y-auto">
					{casesVirtualizerItems.length ? (
						<div
							className="flex flex-col gap-3"
							style={{
								height: casesVirtualizer.getTotalSize(),
							}}
						>
							{casesVirtualizerItems.map((casesVirtualizerItem) => {
								const caseItem = caseItems[casesVirtualizerItem.index];
								if (!caseItem) return null;
								return (
									// biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
									<div
										key={casesVirtualizerItem.key}
										// biome-ignore lint/a11y/useSemanticElements: <explanation>
										role="link"
										tabIndex={0}
										data-index={casesVirtualizerItem.index}
										ref={casesVirtualizer.measureElement}
										className="container absolute top-0 left-0 cursor-pointer"
										style={{
											transform: `translateY(${casesVirtualizerItem.start}px)`,
										}}
										onClick={() =>
											navigate({
												to: "/dashboard/case/view/$id",
												params: { id: caseItem.id },
											})
										}
									>
										<CaseCard caseItem={caseItem} />
									</div>
								);
							})}
						</div>
					) : (
						<>
							{isEmpty ? (
								<EmptyCase addLabel="Add case" onClickAdd={onClickAdd} />
							) : null}
							{isEmptySearch ? (
								<EmptyCase
									title={`No results found for '${debouncedSearch}'`}
								/>
							) : null}
						</>
					)}
				</div>
			</div>
			<ConfirmModal
				open={isOpenConfirmFreeCase}
				onClose={closeConfirmFreeCase}
				title={
					!profile?.case_left
						? "Subscription Required"
						: `You have ${profile?.case_left || 0} free cases left`
				}
				description={
					!profile?.case_left
						? "Dear colleagues, thank you for using HEMI Teams. To continue using the app and enjoy all its features, please begin your subscription now."
						: "Upgrade to Pro for unlimited cases."
				}
				positiveButtonProps={{
					label: !profile?.case_left ? "Subscribe Now" : "Upgrade Now",
					onClick: () => navigate({ to: "/subscription" }),
				}}
				negativeButtonProps={{
					label: `Stay ${upperFirst(profile?.subscription_status)}`,
					onClick: () => {
						closeConfirmFreeCase();
						openAddNewCase();
					},
				}}
			/>
			<ConfirmModal
				open={isOpenAddNewCase}
				onClose={closeAddNewCase}
				title="Add New Case"
				positiveButtonProps={{
					label: "New Patient",
					onClick: () => navigate({ to: "/dashboard/case/create" }),
				}}
				negativeButtonProps={{
					label: "Select Existing Patient",
					onClick: () => navigate({ to: "/dashboard/case/create/exist" }),
				}}
			/>
			<ButtonSpeedDial
				mb={0}
				bottom={0}
				syncProcessing={caseSync.isPending}
				onClickAdd={onClickAdd}
				onClickSync={onClickSync}
			/>
		</div>
	);
}
